import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  Grid2,
  Container,
  useTheme,
  Divider,
  Backdrop,
  CircularProgress,
  Paper,
  Drawer,
  Badge,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SourcingOpsProductMockups from "../../../components/projects/sourcing-ops/SourcingOpsProductMockups";
import OrderUnifiedMockupModal, {
  OrderMockupModalTab,
} from "../../../components/projects/sourcing-ops/OrderUnifiedMockupModal";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import SkuCopyRow from "../../../../../../views/components/common/SkuCopyRow";
import CustomerHeading from "../../../components/projects/projectDetails/CustomerHeading";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppSearchDropDown2 from "../../../../../../views/components/common/filter/AppSearchDropdown2";
import AppDropDown from "../../../../../../views/components/common/filter/AppDropDown";
import {
  activeCatalogSkus,
  catalogSkuForRequest,
  mockupMatchesCatalogSku,
  PRODUCT_LEVEL_SKU,
} from "../../../components/swagstore/mockups/catalogSkuChoices";
import CatalogProductsAPI from "../../../../api/CatalogProductsAPI";
import { FilterValue } from "../../../../../../models/Filters/FilterValue";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import xIcon from "../../../../../../assets/X.png";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageUpload from "../../../../../../views/components/common/upload/ImageUpload";
import cdricon from "../../../../../../assets/cdrIcon.png";
import { useStores } from "../../../../use-stores";
import { EmployeeRoles } from "../../../../../../models/Employees/EmployeeRoles";
import { useNotification } from "../../../../../../utils/NotificationsContext";
import { OrderMockup } from "../../../../../../models/Order/OrderMockup";
import { observer } from "mobx-react";
import ImageAPI from "../../../../api/ImageAPI";
import { MockupComment } from "../../../../../../models/Order/MockupComment";
import { ProductVariant } from "../../../../../../models/Products/ProductVariant";
import ConfirmationPopup from "../../../../../../views/components/common/pop-up/ConfirmationPopup";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ProjectProductComments from "./ProjectProductComments";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CustomToggleSwitch from "../../../../../../views/components/common/switch/Switch";
import PermissionBlockLabel from "../../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { isPodOrderType } from "../../../../../../models/Project/OrderType";
import PodProjectOpsDisabledBanner from "../../../components/projects/PodProjectOpsDisabledBanner";
import ImageGalleryPopup from "../../../../../../views/components/common/pop-up/ImageGalleryPopup";
import OrderProductVariantsDrawer, {
  ProductVariantRowInput,
} from "../../../components/order/OrderProductVariantsDrawer";

const SourcingOpsDetails: React.FC = () => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const [searchParams] = useSearchParams();
  const projectId = Number(searchParams.get("project-id"));
  const productId = Number(searchParams.get("product-id"));
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const {
    userStore,
    orderStore,
    projectSourcingOpsStore,
    shipmentStore,
    productStore,
    projectManagementStore,
    mockupConfigStore,
  } = useStores();
  const [openMockupsModal, setOpenMockupsModal] = useState<boolean>(false);
  const [mockupModalTab, setMockupModalTab] =
    useState<OrderMockupModalTab>("view");
  const [generatorMockupId, setGeneratorMockupId] = useState<
    number | undefined
  >(undefined);
  const [isCommentsFetching, setIsCommentsFetching] = useState(false);
  const [isMainMockupImage, setIsMainMockupImage] = useState(false);
  // const [variantsDropdownOptions, setVariantsDropdownOptions] = useState<
  //   { id: string; sku: string }[]
  // >([]);
  const [confirmationPopup, setConfirmationPopup] = useState<boolean>(false);
  const [confirmationMailToClient, setConfirmationMailToClient] =
    useState(false);
  const [confirmationMailToPrinter, setConfirmationMailToPrinter] =
    useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [openImageGalleryModal, setOpenImageGalleryModal] = useState(false);
  const [isVariantsDrawerOpen, setIsVariantsDrawerOpen] = useState(false);
  const [isCommentsDrawerOpen, setIsCommentsDrawerOpen] = useState(false);
  const [printingFileSku, setPrintingFileSku] = useState(PRODUCT_LEVEL_SKU);
  const [linkedVariantsCount, setLinkedVariantsCount] = useState(0);
  const hasExistingBasicPrintingDetails = Boolean(
    projectSourcingOpsStore.productDetails?.id,
  );
  const canEditExistingPrintingDetails = userStore.hasPermission(
    EmployeeRoles.EDIT_PRODUCT_SOURCING_PRINTING_DETAILS,
  );
  const isPodOrder = isPodOrderType(orderStore.orderBasicDetails?.type_id);
  const isProjectArchived =
    projectManagementStore.currentProjectDetails?.archived === 1;
  // POD still needs printing type / Save Printing Details (used by POD flow).
  // Other sourcing mutations stay locked for POD.
  const isMutationLocked = isPodOrder || isProjectArchived;
  const isBasicPrintingDisabled =
    !projectSourcingOpsStore.productDetails?.customisation_required ||
    isProjectArchived ||
    (hasExistingBasicPrintingDetails && !canEditExistingPrintingDetails);

  useEffect(() => {
    document.body.classList.add("white-background");

    return () => {
      document.body.classList.remove("white-background");
    };
  }, []);

  useEffect(() => {
    if (
      !projectSourcingOpsStore.products ||
      projectSourcingOpsStore.products.length === 0
    ) {
      const getSourcingOpsProducts = () => {
        projectSourcingOpsStore
          .getProjectSourcingOpsProducts(projectId)
          .catch((err) => {
            const errorMessage = err?.response?.data?.message || err.message;
            showError(
              "Error Occurred",
              errorMessage ||
                "Failed to fetch order details, please try again later",
            );
          })
          .finally(() => setIsLoading(false));
      };
      getSourcingOpsProducts();
    }

    if (
      !orderStore.orderBasicDetails ||
      orderStore.orderBasicDetails.id !== Number(projectId)
    ) {
      orderStore.getOrderBasicDetails(projectId).catch((err) => {
        setIsLoading(false);
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage ||
            "Failed to fetch order details, please try again later",
        );
      });
    }
    if (
      !projectManagementStore.currentProjectDetails.order_id ||
      projectManagementStore.currentProjectDetails.order_id !== projectId
    ) {
      projectManagementStore.getProjectDetails(projectId).catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage ||
            "Failed to fetch project details, please try again later",
        );
      });
    }

    projectSourcingOpsStore.getTypesOfPrinting(Number(productId));
    // getProductVariants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsCommentsDrawerOpen(false);
    projectSourcingOpsStore.clearProductComments();
    if (!projectId || !productId) {
      return;
    }
    projectSourcingOpsStore
      .loadUnreadProductCommentsCount(projectId, productId)
      .catch(() => {
        // Badge count is best-effort; drawer will surface errors on open.
      });
  }, [projectId, productId]);

  const getSourcingDetails = () => {
    setIsLoading(true);
    projectSourcingOpsStore
      .getSourcingOpsProductDetails(projectId, productId)
      .then((res) => {
        if (res && res.mockups?.length > 0) {
          setProductMockup(res.mockups);
        } else {
          setProductMockup([]);
        }
      })
      .catch((error) => {
        const errorMessage = error?.response?.data?.message || error.message;
        showError(
          "Error Occurred",
          errorMessage || "Failed to upload mockups, please try again later",
        );
      })
      .finally(() => setIsLoading(false));
  };

  // const getProductVariants = () => {
  //   productStore
  //     .getProductDetailsById(productId)
  //     .then(() => {
  //       if ((productStore.productDetails?.variants ?? []).length > 0) {
  //         const options = generateVariantCombinations(
  //           productStore.productDetails?.variants ?? [],
  //         );
  //         setVariantsDropdownOptions(options);
  //       }
  //     })
  //     .catch((err) => {
  //       const errorMessage = err?.response?.data?.message || err.message;
  //       showError(
  //         "Error Occurred",
  //         errorMessage ||
  //           "Failed to fetch product details, please try again later",
  //       );
  //     });
  // };

  const handleShowOnStorefrontUpdate = (ids: number[], showOnStorefront: boolean) => {
    if (ids.length === 0) return Promise.resolve();
    setIsLoading(true);
    return Promise.all(
      ids.map((id) => projectSourcingOpsStore.setMockupShowOnStorefront(id, showOnStorefront)),
    )
      .then((results) => {
        if (results.some((res) => !res?.success)) {
          throw new Error("Failed to update storefront visibility");
        }
        const idSet = new Set(ids);
        const updatedMockups = orderStore.productMockup.map((mockup) =>
          mockup.id != null && idSet.has(mockup.id)
            ? { ...mockup, show_on_storefront: showOnStorefront ? 1 : 0 }
            : mockup,
        );
        setProductMockup(updatedMockups);
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage || "Failed to update storefront visibility",
        );
        throw err;
      })
      .finally(() => setIsLoading(false));
  };

  const handleMockupPriorityUpdate = (id: number) => {
    setIsLoading(true);
    projectSourcingOpsStore
      .updateMockupPriority(projectId, productId, id)
      .then((res) => {
        if (res.success) {
          const updatedMockups = orderStore.productMockup.map((mockup) => ({
            ...mockup,
            priority: mockup.id === id ? 1 : 0,
          }));
          setProductMockup(updatedMockups);
        }
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage ||
            "Failed to fetch product details, please try again later",
        );
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    getSourcingDetails();
    // getProductVariants();
    projectSourcingOpsStore.getTypesOfPrinting(Number(productId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const setProductMockup = (mockups: OrderMockup[]) => {
    setIsLoading(true);
    orderStore.mockupComment = [];
    orderStore.setProductMockups(mockups);
    setIsLoading(false);
  };

  const fetchMockupComment = (mockupId: number) => {
    if (mockupId) {
      orderStore
        .fetchCommentsForProductMockups(mockupId)
        .then()
        .catch((err) => {
          const errorMessage = err?.response?.data?.message || err.message;
          showError(
            "Error Occurred",
            errorMessage || "Failed to fetch comment, please try again later",
          );
        })
        .finally(() => {
          setIsCommentsFetching(false);
        });
    } else {
      setIsCommentsFetching(false);
      showError("Error Occurred", "Failed to find mockup id");
    }
  };

  const postMockupComment = (mockupComment: MockupComment) => {
    if (mockupComment.comments && mockupComment.mockup_id) {
      orderStore
        .postCommentForMockup(mockupComment)
        .then(() => {
          orderStore.updateCommentLocally(mockupComment);
        })
        .catch((err) => {
          const errorMessage = err?.response?.data?.message || err.message;
          showError(
            "Error Occurred",
            errorMessage || "Failed to send comment, please try again later",
          );
        })
        .finally(() => {
          setIsCommentsFetching(false);
        });
    } else {
      setIsCommentsFetching(false);
      showError("Error Occurred", "Failed to find comment OR mockup id");
    }
  };

  const deleteMockup = (mockupId: number) => {
    setIsLoading(true);
    if (mockupId) {
      orderStore
        .deleteMockup(mockupId, projectId)
        .then(() => {
          if (orderStore.productMockup.length > 0) {
            orderStore.removeMockupImageLocally(mockupId);
          } else {
            showSuccess("Please refresh your page to see the updates");
          }
        })
        .catch((err) => {
          const errorMessage = err?.response?.data?.message || err.message;
          showError(
            "Error Occurred",
            errorMessage || "Failed to fetch comment, please try again later",
          );
        })
        .finally(() => {
          setConfirmationPopup(false);
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
      showError("Error Occurred", "Failed to find mockup id");
    }
  };

  const handleApproveMockupAction = (
    mockupId: number,
    imageUrl?: string,
    approvalNote?: string,
  ) => {
    setIsLoading(true);
    if (mockupId) {
      orderStore
        .approveMockup(
          mockupId,
          projectId,
          approvalNote || projectSourcingOpsStore.approvalNote,
          imageUrl ? [imageUrl] : undefined,
        )
        .then(() => {
          showSuccess("Mockup approved successfully");
          projectSourcingOpsStore.clearApprovalData();
        })
        .catch((err) => {
          const errorMessage = err?.response?.data?.message || err.message;
          showError(
            "Error Occurred",
            errorMessage || "Failed to approve mockup, please try again later",
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
      showError("Error Occurred", "Failed to find mockup id");
    }
  };

  const handleRejectMockupAction = (
    mockupId: number,
    rejectionNote?: string,
    imageUrl?: string,
  ) => {
    setIsLoading(true);
    if (mockupId) {
      orderStore
        .rejectMockup(
          mockupId,
          projectId,
          rejectionNote || projectSourcingOpsStore.rejectionNote,
          imageUrl ? [imageUrl] : undefined,
        )
        .then(() => {
          showSuccess("Mockup rejected successfully");
          projectSourcingOpsStore.clearApprovalData();
        })
        .catch((err) => {
          const errorMessage = err?.response?.data?.message || err.message;
          showError(
            "Error Occurred",
            errorMessage || "Failed to reject mockup, please try again later",
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
      showError("Error Occurred", "Failed to find mockup id");
    }
  };

  const handleNavigation = (direction: "back" | "forward") => {
    if (
      !projectSourcingOpsStore.products ||
      projectSourcingOpsStore.products.length === 0
    )
      return;

    const productList = projectSourcingOpsStore.products;
    const currentIndex = productList.findIndex(
      (product) => product.id === productId,
    );

    if (currentIndex === -1) return;

    let newIndex = currentIndex;

    if (direction === "back") {
      newIndex = currentIndex > 0 ? currentIndex - 1 : productList.length - 1;
    } else {
      newIndex = currentIndex < productList.length - 1 ? currentIndex + 1 : 0;
    }

    const newProduct = productList[newIndex];

    navigate(
      `/projects/sourcing-ops-details?project-id=${projectId}&product-id=${newProduct.id}`,
    );
  };

  const [activePrintingSkus, setActivePrintingSkus] = useState<Set<string>>(
    new Set(),
  );

  useEffect(() => {
    setPrintingFileSku(PRODUCT_LEVEL_SKU);
  }, [productId]);

  useEffect(() => {
    if (!productId) {
      setActivePrintingSkus(new Set());
      return;
    }
    let cancelled = false;
    new CatalogProductsAPI()
      .fetchVariants(productId)
      .then((rows) => {
        if (cancelled) return;
        setActivePrintingSkus(
          new Set(activeCatalogSkus(rows).map((sku) => sku.toUpperCase())),
        );
      })
      .catch(() => {
        if (!cancelled) setActivePrintingSkus(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [productId]);

  const printingSkuChoices = useMemo(() => {
    const seen = new Set<string>();
    const choices = [{ id: PRODUCT_LEVEL_SKU, value: "All SKUs" }];
    for (const variant of projectSourcingOpsStore.productDetails?.size_mix ?? []) {
      const sku = String(variant.sku ?? "").trim();
      const key = sku.toUpperCase();
      if (!sku || seen.has(key) || !activePrintingSkus.has(key)) continue;
      seen.add(key);
      choices.push({ id: sku, value: sku });
    }
    return choices;
  }, [projectSourcingOpsStore.productDetails?.size_mix, activePrintingSkus]);

  const printingFilesForSku = (
    projectSourcingOpsStore.productDetails?.printing_files ?? []
  ).filter((file) => mockupMatchesCatalogSku(file.variant_sku, printingFileSku));
  const printingSkuSelected = catalogSkuForRequest(printingFileSku) != null;

  const handleDeleteFile = (id: number) => {
    setIsLoading(true);
    projectSourcingOpsStore
      .deleteFilesForPrinting(projectId, productId, id)
      .catch((error) => {
        showError(
          "Error occurred",
          error?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => setIsLoading(false));
  };

  const handleUpload = (images: File[]) => {
    const sku = catalogSkuForRequest(printingFileSku);
    if (!sku) {
      showError(
        "SKU required",
        "Select a SKU. New printing files are saved against one SKU.",
      );
      return;
    }
    if (images.length === 0) return;
    setIsLoading(true);
    const imageAPI = new ImageAPI();
    imageAPI
      .uploadCdr(images)
      .then((uploadedUrl) =>
        projectSourcingOpsStore.saveFilesForPrinting(
          projectId,
          productId,
          uploadedUrl,
          sku,
        ),
      )
      .catch((err) => {
        showError(
          "Error occurred",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => setIsLoading(false));
  };

  const handleSaveBasicDetails = () => {
    setIsLoading(true);
    projectSourcingOpsStore
      .saveSourcingOpsBasicDetails(projectId, productId)
      .catch((error) =>
        showError(
          "Error occurred",
          error?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        ),
      )
      .finally(() => setIsLoading(false));
  };

  const handleSaveAdditionalDetails = () => {
    const validate =
      projectSourcingOpsStore.productDetails?.size_mix?.every(
        (variant) => Boolean(variant.sku) && Boolean(variant.quantity),
      ) ?? true;
    if (!validate) {
      return;
    } else {
      setIsLoading(true);
      projectSourcingOpsStore
        .saveSourcingOpsAdditionalDetails(projectId, productId)
        .catch((error) =>
          showError(
            "Error occurred",
            error?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          ),
        )
        .finally(() => setIsLoading(false));
    }
  };

  const generateVariantCombinations = (variants: ProductVariant[]) => {
    const groupedVariants: Record<string, string[]> = {};

    variants.forEach((variant) => {
      if (!groupedVariants[variant.options_name!]) {
        groupedVariants[variant.options_name!] = [];
      }
      groupedVariants[variant.options_name!].push(variant.title);
    });

    const variantGroups = Object.values(groupedVariants);

    const generateCombinations = (
      arrays: string[][],
      prefix: string[] = [],
    ): string[][] => {
      if (!arrays.length) return [prefix];

      const [first, ...rest] = arrays;
      return first.flatMap((value) =>
        generateCombinations(rest, [...prefix, value]),
      );
    };

    return generateCombinations(variantGroups).map((combination, index) => ({
      id: combination.join("-"),
      sku: combination.join("-"),
    }));
  };

  const handleSendEmailToClient = () => {
    setIsLoading(true);
    projectSourcingOpsStore
      .sendMailToClient(projectId)
      .then((res) => {
        if (res.success) {
          showSuccess("Email sent successfully");
        }
      })
      .catch((error) =>
        showError(
          "Error occurred",
          error?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        ),
      )
      .finally(() => setIsLoading(false));
  };

  const handleSendEmailToPrinter = () => {
    setIsLoading(true);
    projectSourcingOpsStore
      .sendMailToPrinter(projectId, productId)
      .then((res) => {
        if (res.success) {
          showSuccess("Email sent successfully");
        }
      })
      .catch((error) =>
        showError(
          "Error occurred",
          error?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        ),
      )
      .finally(() => setIsLoading(false));
  };

  const setCustomization = (customisation: boolean) => {
    setIsLoading(true);
    projectSourcingOpsStore
      .editCustomization(projectId, productId, customisation)
      .catch((error) => {
        const errorMessage = error?.response?.data?.message || error.message;
        showError(
          "Error Occurred",
          errorMessage ||
            "Failed to update customization, please try again later",
        );
      })
      .finally(() => setIsLoading(false));
  };

  const loadMockupGeneratorData = () => {
    mockupConfigStore.resetGeneratedMockupData();
    const catalogueChanged = mockupConfigStore.catalogueId !== productId;
    if (catalogueChanged) {
      mockupConfigStore.resetProductData();
      mockupConfigStore.setCatalogueId(productId);
    }

    void mockupConfigStore.getPlacementTypes().catch((e) => {
      showError(
        "Error",
        e?.response?.data?.message || "Failed to load placement types",
      );
    });

    void mockupConfigStore.getPSDTemplates(productId).catch((e) => {
      showError(
        "Error",
        e?.response?.data?.message || "Failed to load PSD templates",
      );
    });

    void projectSourcingOpsStore
      .loadMockupGeneratorLogos(orderStore.orderBasicDetails?.customer?.id)
      .catch(() => {
        // Sourcing ops users may lack swagstore view permission; local upload still works.
      });
  };

  const openMockupGenerator = (mockupId?: number) => {
    setGeneratorMockupId(mockupId);
    setOpenMockupsModal(true);
    setMockupModalTab("generate");
    loadMockupGeneratorData();
  };

  const closeMockupGenerator = () => {
    setOpenMockupsModal(false);
    setMockupModalTab("view");
    setGeneratorMockupId(undefined);
    projectSourcingOpsStore.resetMockupGeneratorLogos();
    projectSourcingOpsStore.setGeneratingOrderMockup(false);
    projectSourcingOpsStore.setUpdatingOrderMockupId(null);
  };

  const completeMockupWorkflow = () => {
    setGeneratorMockupId(undefined);
    projectSourcingOpsStore.resetMockupGeneratorLogos();
    projectSourcingOpsStore.setGeneratingOrderMockup(false);
    projectSourcingOpsStore.setUpdatingOrderMockupId(null);

    projectSourcingOpsStore
      .getSourcingOpsProductDetails(projectId, productId)
      .then((res) => {
        if (res?.mockups?.length) {
          orderStore.mockupComment = [];
          orderStore.setProductMockups(res.mockups);
          let latestId = 0;
          for (const mockup of res.mockups) {
            const id = Number(mockup.id);
            if (Number.isFinite(id) && id > latestId) {
              latestId = id;
            }
          }
          if (latestId > 0) {
            projectSourcingOpsStore.setSelectedMockupId(latestId);
          }
        } else {
          orderStore.mockupComment = [];
          orderStore.setProductMockups([]);
        }
      })
      .catch(() => {})
      .finally(() => {
        setMockupModalTab("view");
      });
  };

  const handleMockupTabChange = (
    tab: OrderMockupModalTab,
    mockupId?: number,
  ) => {
    setMockupModalTab(tab);
    setGeneratorMockupId(tab === "view" ? undefined : mockupId);
    if (tab !== "view") {
      loadMockupGeneratorData();
    }
  };

  const handleOrderGenerateMockup = async (payload: {
    psd_template_id: number;
    logoFile?: File | null;
    logoUrl?: string | null;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
    template_required?: boolean;
    printing_type?: string;
    logo_size?: string;
    rgb_code?: string;
    cmyk_code?: string;
    variant_sku: string;
  }) => {
    projectSourcingOpsStore.setGeneratingOrderMockup(true);

    try {
      let logoUrl = payload.logoUrl ?? null;
      if (!logoUrl) {
        if (!payload.logoFile) {
          showError("Error", "Please upload or select a logo");
          return;
        }
        const imageAPI = new ImageAPI();
        logoUrl = await imageAPI.uploadFile(payload.logoFile, "mockup-logos");
      }
      if (!logoUrl) {
        showError("Error", "Please upload or select a logo");
        return;
      }

      const result = await orderStore.generateMockupForProduct({
        order_id: projectId,
        product_id: productId,
        psd_template_id: payload.psd_template_id,
        logo_url: logoUrl,
        x: payload.x,
        y: payload.y,
        width: payload.width,
        height: payload.height,
        rotation: payload.rotation,
        template_required: payload.template_required ?? false,
        printing_type: payload.printing_type,
        logo_size: payload.logo_size,
        rgb_code: payload.rgb_code,
        cmyk_code: payload.cmyk_code,
        variant_sku: payload.variant_sku,
        mockup_id: generatorMockupId,
      });
      if (!result) return;

      completeMockupWorkflow();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      showError(
        "Generate failed",
        err?.response?.data?.message || "Failed to generate mockup",
      );
    } finally {
      projectSourcingOpsStore.setGeneratingOrderMockup(false);
    }
  };

  const handleOrderUploadLogoToLibrary = (file: File) => {
    void projectSourcingOpsStore
      .uploadMockupGeneratorLogo(
        file,
        orderStore.orderBasicDetails?.customer?.id,
      )
      .catch((e) => {
        showError(
          "Upload failed",
          e?.response?.data?.message || "Failed to upload logo",
        );
      });
  };

  const handleOrderDeleteUploadedLogo = async (logoId: number) => {
    try {
      await projectSourcingOpsStore.deleteMockupGeneratorLogo(
        logoId,
        orderStore.orderBasicDetails?.customer?.id,
      );
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      showError(
        "Delete failed",
        err?.response?.data?.message || "Failed to delete logo",
      );
    }
  };

  const handleOrderManualUploadMockup = async (payload: {
    id: number;
    file: File;
    variant_sku: string;
  }) => {
    projectSourcingOpsStore.setUpdatingOrderMockupId(payload.id);
    try {
      const imageAPI = new ImageAPI();
      const uploadedUrls = await imageAPI.uploadImage(
        [payload.file],
        "product-images",
      );
      const url = uploadedUrls[0];
      if (!url) {
        showError("Upload failed", "Failed to upload mockup image");
        return;
      }
      await orderStore.uploadMockupsForProduct(
        {
          product_id: productId,
          image_urls: [url],
          id: payload.id,
          variant_sku: payload.variant_sku,
          source: "manual",
        },
        projectId,
      );
      completeMockupWorkflow();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      showError(
        "Upload failed",
        err?.response?.data?.message || "Failed to upload mockup",
      );
    } finally {
      projectSourcingOpsStore.setUpdatingOrderMockupId(null);
    }
  };

  const handleOrderUploadMockupFiles = async (
    files: File[],
    variantSku: string,
  ) => {
    projectSourcingOpsStore.setGeneratingOrderMockup(true);
    try {
      const imageAPI = new ImageAPI();
      const uploadedUrls = await imageAPI.uploadImage(files, "product-images");
      if (!uploadedUrls.length) {
        showError("Upload failed", "Failed to upload mockup image");
        return;
      }
      await orderStore.uploadMockupsForProduct(
        {
          product_id: productId,
          image_urls: uploadedUrls,
          variant_sku: variantSku,
          source: "manual",
        },
        projectId,
      );
      completeMockupWorkflow();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      showError(
        "Upload failed",
        err?.response?.data?.message || "Failed to upload mockup",
      );
    } finally {
      projectSourcingOpsStore.setGeneratingOrderMockup(false);
    }
  };

  const uploadMockupUpdate = (mockupId: number, uploadedUrl: string[]) => {
    setIsLoading(true);
    const url = uploadedUrl[0];
    if (productId && uploadedUrl.length > 0) {
      orderStore
        .uploadMockupsForProduct(
          {
            product_id: productId,
            image_urls: [url],
            id: mockupId,
          },
          projectId,
        )
        .then(() => {
          getSourcingDetails();
        })
        .catch((err) => {
          const errorMessage = err?.response?.data?.message || err.message;
          showError(
            "Error Occurred",
            errorMessage || "Failed to upload mockups, please try again later",
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      showError(
        "Error Occurred",
        "Failed to find product id or mockups or order id",
      );
    }
  };

  const selectedProduct =
    projectSourcingOpsStore.products?.find((p) => p.id === productId) || null;

  useEffect(() => {
    const sizeMixCount =
      projectSourcingOpsStore.productDetails?.size_mix?.length ?? 0;
    setLinkedVariantsCount(sizeMixCount);
  }, [projectSourcingOpsStore.productDetails?.size_mix, productId]);

  const openVariantsDrawer = () => {
    setIsVariantsDrawerOpen(true);
  };

  const closeVariantsDrawer = () => {
    setIsVariantsDrawerOpen(false);
    getSourcingDetails();
    // getProductVariants();
  };

  const handleVariantsSubmit = (_variantRows: ProductVariantRowInput[]) => {};

  return (
    <>
      <Backdrop
        sx={(theme) => ({
          color: theme.palette.customColors.white,
          zIndex: theme.zIndex.drawer + 1,
        })}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {userStore.hasPermission(EmployeeRoles.VIEW_ORDER) ? (
        <>
          <Container
            maxWidth="xl"
            sx={{
              pl: { xl: "6.25rem", lg: "4.063rem" },
              pr: { xl: "6.25rem", lg: "4.063rem" },
            }}
          >
            <Box sx={{ margin: "auto", mb: "1.625rem" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  cursor: "pointer",
                  color: theme.palette.customColors.greyTextColor,
                  mb: 2,
                  mt: 2,
                  maxWidth: "3.75rem",
                  paddingLeft: { xl: 3.2, lg: 3.2, md: 3.2, sm: 0, xs: 0 },
                  "&:hover, &:active": {
                    color: theme.palette.customColors.greyTextColor,
                  },
                }}
                onClick={() => {
                  navigate(`/projects/${projectId}?tab_index=1`);
                }}
              >
                <ArrowBackIcon sx={{ fontSize: "1rem" }} />
                <span style={{ fontSize: "0.875rem", fontWeight: "600" }}>
                  Back
                </span>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  position: "relative",
                  width: "100%",
                  gap: "20px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "@media (max-width: 900px)": {
                      display: "none",
                    },
                  }}
                >
                  <IconButton
                    onClick={() => handleNavigation("back")}
                    disabled={
                      projectSourcingOpsStore.checkProductIndex(productId) === 0
                    }
                    sx={{
                      mt: 1,
                      ml: { xs: "-26px", lg: "-26px", md: "-26px" },
                      border: `2px solid ${theme.palette.customColors.disabledBorderColor}`,
                      width: "35px",
                      height: "35px",
                      display: { xs: "flex", md: "inline-flex" },
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ArrowBackIosIcon
                      sx={{ fontSize: { xs: "1rem", md: "1rem", sm: "1rem" } }}
                      style={{ marginLeft: "5px" }}
                    />
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    flexGrow: 1,
                  }}
                >
                  {(() => {
                    const product = projectSourcingOpsStore.products?.find(
                      (p) => p.id === productId,
                    );
                    return (
                      <>
                        <Box
                          sx={{
                            maxWidth: "450px",
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                          }}
                        >
                          <CustomerHeading
                            brandName={
                              orderStore.orderBasicDetails?.customer?.name || ""
                            }
                            label={orderStore.orderBasicDetails?.label || ""}
                            moduleName={product?.name || ""}
                            orderId={projectId}
                            productId={productId || product?.id}
                            quantity={product?.quantity || null}
                          />
                        </Box>
                      </>
                    );
                  })()}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "1.688rem",
                      mt: 1,
                    }}
                  >
                    <Tooltip title="Product comments">
                      <IconButton
                        onClick={() => setIsCommentsDrawerOpen(true)}
                        sx={{
                          border: `1.5px solid ${theme.palette.customColors.disabledBorderColor}`,
                          width: 40,
                          height: 40,
                          backgroundColor: theme.palette.common.white,
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors.lightPurple,
                          },
                        }}
                      >
                        <Badge
                          badgeContent={
                            projectSourcingOpsStore.unreadProductCommentsCount ||
                            undefined
                          }
                          color="primary"
                          max={99}
                        >
                          <ChatBubbleOutlineIcon
                            sx={{
                              fontSize: "1.25rem",
                              color: theme.palette.customColors.purple,
                            }}
                          />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                    {(userStore.hasPermission(EmployeeRoles.CREATE_PROJECT) ||
                      userStore.hasPermission(EmployeeRoles.EDIT_PROJECT)) && (
                      <>
                        <AppButton
                          label="Mail to client"
                          onClick={() => setConfirmationMailToClient(true)}
                          fontWeight={600}
                          fontSize="1rem"
                          variant="grayscale"
                          disabled={
                            Boolean(
                              projectSourcingOpsStore.productDetails!
                                .customisation_required,
                            ) === false ||
                            isMutationLocked
                          }
                        />
                        <AppButton
                          label="Mail to printer"
                          onClick={() => setConfirmationMailToPrinter(true)}
                          fontWeight={600}
                          fontSize="1rem"
                          variant="secondary"
                          disabled={
                            Boolean(
                              projectSourcingOpsStore.productDetails!
                                .customisation_required,
                            ) === false ||
                            (Boolean(
                              projectSourcingOpsStore.productDetails!
                                .customisation_required,
                            ) === true &&
                              (projectSourcingOpsStore.productDetails!
                                .printing_type_id === undefined ||
                                projectSourcingOpsStore.productDetails!
                                  .printing_cost === undefined ||
                                (projectSourcingOpsStore.productDetails
                                  ?.printing_cost || 0) <= 0 ||
                                projectSourcingOpsStore.productDetails!
                                  .printer_id === undefined ||
                                projectSourcingOpsStore.productDetails!
                                  .printing_files.length === 0)) ||
                            isMutationLocked
                          }
                        />
                      </>
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "@media (max-width: 900px)": {
                      display: "none",
                    },
                  }}
                >
                  <IconButton
                    onClick={() => handleNavigation("forward")}
                    disabled={
                      projectSourcingOpsStore.checkProductIndex(productId) ===
                      projectSourcingOpsStore.products?.length - 1
                    }
                    sx={{
                      mt: 1,
                      mr: { xs: "-26px", lg: "-26px", md: "-26px" },
                      border: `2px solid ${theme.palette.customColors.disabledBorderColor}`,
                      width: "35px",
                      height: "35px",
                      display: { xs: "flex", md: "inline-flex" },
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ArrowForwardIosIcon
                      sx={{ fontSize: { xs: "1rem", md: "1rem", sm: "1rem" } }}
                      style={{ marginLeft: "2px" }}
                    />
                  </IconButton>
                </Box>
              </Box>
              {isPodOrder ? <PodProjectOpsDisabledBanner /> : null}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  "@media (min-width: 900px)": {
                    display: "none",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IconButton
                    onClick={() => handleNavigation("back")}
                    disabled={
                      projectSourcingOpsStore.checkProductIndex(productId) === 0
                    }
                    sx={{
                      mt: 1,
                      ml: { xs: "-26px", lg: "-26px", md: "-26px" },
                      border: `2px solid ${theme.palette.customColors.disabledBorderColor}`,
                      width: "35px",
                      height: "35px",
                      display: { xs: "flex", md: "inline-flex" },
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ArrowBackIosIcon
                      sx={{ fontSize: { xs: "1rem", md: "1rem", sm: "1rem" } }}
                      style={{ marginLeft: "4px" }}
                    />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IconButton
                    onClick={() => handleNavigation("forward")}
                    disabled={
                      projectSourcingOpsStore.checkProductIndex(productId) ===
                      projectSourcingOpsStore.products?.length - 1
                    }
                    sx={{
                      mt: 1,
                      border: `2px solid ${theme.palette.customColors.disabledBorderColor}`,
                      width: "35px",
                      height: "35px",
                      display: { xs: "flex", md: "inline-flex" },
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ArrowForwardIosIcon
                      sx={{ fontSize: { xs: "1rem", md: "1rem", sm: "1rem" } }}
                      style={{ marginLeft: "2px" }}
                    />
                  </IconButton>
                </Box>
              </Box>
            </Box>

            <Box sx={{ margin: "auto" }} pl={3} pr={3}>
              <Divider sx={{ mt: 2, mb: 3.2 }} />
              <Grid2
                container
                spacing={4}
                columns={{ lg: 12, xs: 12 }}
                sx={{
                  display: { xl: "flex", lg: "flex", md: "block" },
                  justifyContent: { xl: "space-between", lg: "space-between" },
                }}
              >
                <Grid2 size={{ xl: 4, lg: 5.4, xs: 12 }}>
                  <Box display={"flex"} gap={2}>
                    <CustomTypography variant="heading5" fontWeight={600}>
                      Customization
                    </CustomTypography>
                    {(userStore.hasPermission(EmployeeRoles.CREATE_PROJECT) ||
                      userStore.hasPermission(EmployeeRoles.EDIT_PROJECT)) && (
                      <>
                        <CustomToggleSwitch
                          checked={
                            !!projectSourcingOpsStore.productDetails
                              ?.customisation_required
                          }
                          onChange={(e) => {
                            setCustomization(e.target.checked);
                          }}
                          disabled={isProjectArchived}
                        />
                      </>
                    )}
                  </Box>
                  <Box
                    mt={2.5}
                    sx={{
                      maxHeight: "59.125rem",
                      overflow: "auto",
                      "&::-webkit-scrollbar": {
                        width: "2px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: theme.palette.customColors.blackBorder,
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor:
                          theme.palette.customColors.greyBackground,
                      },
                    }}
                  >
                    <SourcingOpsProductMockups
                      canDeleteMockup={userStore.hasPermission(
                        EmployeeRoles.DELETE_MOCKUP,
                      )}
                      archived={isMutationLocked ? 1 : 0}
                      selectedMockup={projectSourcingOpsStore.selectedMockupId}
                      setSelectedMockup={(id: number) => {
                        projectSourcingOpsStore.setSelectedMockupId(id);
                      }}
                      mockups={orderStore.productMockup || []}
                      handleCommentClick={(id: number) => {
                        projectSourcingOpsStore.setSelectedMockupId(id);
                        setOpenMockupsModal(true);
                        setMockupModalTab("view");
                      }}
                      handleDeleteClick={(id: number) => {
                        setConfirmationPopup(true);
                        projectSourcingOpsStore.setSelectedMockupId(id);
                      }}
                      openImagePreview={(image_url: string) => {
                        setPreviewImage(image_url);
                        setOpenImageGalleryModal(true);
                      }}
                      handleMockupPriorityChange={(id: number) =>
                        handleMockupPriorityUpdate(id)
                      }
                      handleShowOnStorefrontChange={(id, showOnStorefront) =>
                        handleShowOnStorefrontUpdate(id, showOnStorefront)
                      }
                      uploadMockupUpdate={(
                        mockupId: number,
                        uploadedUrl: string[],
                      ) => {
                        uploadMockupUpdate(mockupId, uploadedUrl);
                      }}
                      onGenerateClick={() => openMockupGenerator()}
                      onGenerateMockupClick={(id) => openMockupGenerator(id)}
                      handleApproveMockup={handleApproveMockupAction}
                      handleRejectMockup={handleRejectMockupAction}
                    />
                  </Box>
                </Grid2>

                <Grid2
                  size={{ xl: 7.8, lg: 6, xs: 12 }}
                  mt={{ xl: 0.5, lg: 0, md: 2, sm: 2, xs: 2 }}
                >
                  <Box display={"flex"} mb={"1.813rem"}>
                    <CustomTypography variant="heading5" fontWeight={600}>
                      Product Details
                    </CustomTypography>
                  </Box>
                  {/*  */}
                  <Grid2
                    container
                    spacing={{ xl: 10 }}
                    columns={{ lg: 12, xs: 12 }}
                    sx={{
                      display: {
                        xs: "block",
                        lg: "block",
                        xl: "flex",
                      },
                      flexWrap: "wrap",
                      justifyContent: { xl: "space-between" },
                    }}
                  >
                    <Grid2
                      size={{ xl: 6, lg: 12, xs: 12 }}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "start",
                      }}
                    >
                      <Box>
                        <Box>
                          <CustomTypography
                            variant="heading6"
                            color={theme.palette.customColors.greyTextColor}
                          >
                            Printing Type
                          </CustomTypography>
                          <Box mt={"1rem"} sx={{ width: "100%" }}>
                            <AppSearchDropDown2
                              isMultiSelect={false}
                              title={"Select Printing Type"}
                              local={false}
                              borderColor={
                                theme.palette.customColors.greyDarkborder
                              }
                              list={projectSourcingOpsStore.printingTypes.map(
                                (item) => {
                                  return {
                                    id: String(item.id),
                                    value: item.type!,
                                  };
                                },
                              )}
                              handleSelect={(selected: FilterValue[]) => {
                                if (
                                  selected.length > 0 &&
                                  selected[0].id !== undefined
                                ) {
                                  projectSourcingOpsStore.updateProductDetails(
                                    "printing_type_id",
                                    Number(selected[0].id),
                                  );
                                  projectSourcingOpsStore.updateProductDetails(
                                    "printing_type",
                                    selected[0].value,
                                  );
                                } else {
                                  projectSourcingOpsStore.updateProductDetails(
                                    "printing_type_id",
                                    null,
                                  );
                                  projectSourcingOpsStore.updateProductDetails(
                                    "printing_type",
                                    null,
                                  );
                                }
                              }}
                              value={
                                projectSourcingOpsStore.productDetails
                                  ?.printing_type_id
                                  ? [
                                      {
                                        id: String(
                                          projectSourcingOpsStore.productDetails
                                            .printing_type_id,
                                        ),
                                        value: String(
                                          projectSourcingOpsStore.productDetails
                                            .printing_type,
                                        ),
                                      },
                                    ]
                                  : undefined
                              }
                              disabled={isBasicPrintingDisabled}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Grid2>

                    <Grid2
                      size={{ xl: 6, lg: 12, xs: 12 }}
                      sx={{
                        mt: { xl: 0, lg: 2.5, sm: 3, xs: 3 },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "end",
                      }}
                    >
                      <Box>
                        <Box>
                          <CustomTypography
                            variant="heading6"
                            color={theme.palette.customColors.greyTextColor}
                          >
                            Printer Name
                          </CustomTypography>
                          <Box mt={1.8} sx={{ width: "100%" }}>
                            <AppSearchDropDown2
                              isMultiSelect={false}
                              title={"Select Printer"}
                              local={false}
                              borderColor={
                                theme.palette.customColors.greyDarkborder
                              }
                              handleSearch={(searchText) => {
                                shipmentStore.listPrinterVendors(searchText);
                              }}
                              list={shipmentStore.deliveryPartners.map(
                                (item) => {
                                  return {
                                    id: String(item.delivery_partner_id),
                                    value: item.delivery_partner_name!,
                                  };
                                },
                              )}
                              handleSelect={(selectedVendor: FilterValue[]) => {
                                if (
                                  selectedVendor.length > 0 &&
                                  selectedVendor[0].id !== undefined
                                ) {
                                  projectSourcingOpsStore.updateProductDetails(
                                    "printer_id",
                                    Number(selectedVendor[0].id),
                                  );
                                  projectSourcingOpsStore.updateProductDetails(
                                    "vendor_name",
                                    selectedVendor[0].value,
                                  );
                                } else {
                                  projectSourcingOpsStore.updateProductDetails(
                                    "printer_id",
                                    null,
                                  );
                                  projectSourcingOpsStore.updateProductDetails(
                                    "vendor_name",
                                    null,
                                  );
                                }
                              }}
                              value={
                                projectSourcingOpsStore.productDetails
                                  ?.printer_id
                                  ? [
                                      {
                                        id: String(
                                          projectSourcingOpsStore.productDetails
                                            .printer_id,
                                        ),
                                        value: String(
                                          projectSourcingOpsStore.productDetails
                                            .vendor_name,
                                        ),
                                      },
                                    ]
                                  : undefined
                              }
                              disabled={isBasicPrintingDisabled}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Grid2>
                  </Grid2>

                  {/*  */}
                  <Grid2
                    container
                    spacing={{ xl: 10 }}
                    columns={{ lg: 12, xs: 12 }}
                    mt={"1.875rem"}
                    sx={{
                      display: {
                        xs: "block",
                        lg: "block",
                        xl: "flex",
                      },
                      flexWrap: "wrap",
                      justifyContent: { xl: "space-between" },
                    }}
                  >
                    <Grid2
                      size={{ xl: 6, lg: 12, xs: 12 }}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "start",
                      }}
                    >
                      <CustomTypography
                        variant="heading6"
                        color={theme.palette.customColors.greyTextColor}
                      >
                        Printing Cost
                      </CustomTypography>
                      <CustomTextField
                        type="number"
                        customStyles={{
                          width: "100%",
                          border: `0.1px solid ${theme.palette.customColors.greyDarkborder}`,
                          borderRadius: "4px",
                          marginTop: "1rem",
                        }}
                        value={
                          projectSourcingOpsStore.productDetails
                            ?.printing_cost || ""
                        }
                        placeholder=""
                        onSearch={(printingCost) => {
                          projectSourcingOpsStore.updateProductDetails(
                            "printing_cost",
                            Number(printingCost),
                          );
                        }}
                        label=""
                        disabled={isBasicPrintingDisabled}
                      />
                    </Grid2>
                    <Grid2
                      size={{ xl: 6, lg: 12, xs: 12 }}
                      sx={{
                        mt: { xl: 0, lg: 2.5, sm: 3, xs: 3 },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "end",
                      }}
                    >
                      {(userStore.hasPermission(EmployeeRoles.CREATE_PROJECT) ||
                        userStore.hasPermission(EmployeeRoles.EDIT_PROJECT) ||
                        userStore.hasPermission(
                          EmployeeRoles.EDIT_PRODUCT_SOURCING_DETAILS,
                        )) && (
                        <AppButton
                          label="Save Printing Details"
                          onClick={() => handleSaveBasicDetails()}
                          fontWeight={600}
                          fontSize="1rem"
                          variant="primary"
                          customStyles={{ width: "100%" }}
                          disabled={isBasicPrintingDisabled}
                        />
                      )}
                    </Grid2>
                  </Grid2>

                  <Divider sx={{ mt: "2.125rem", mb: "1.688rem" }} />
                  {/*  */}
                  {(() => {
                    const product = projectSourcingOpsStore.products?.find(
                      (p) => p.id === productId,
                    );

                    return (
                      <>
                        {product &&
                          projectSourcingOpsStore.productDetails &&
                          (Number(
                            projectSourcingOpsStore.productDetails
                              ?.product_details?.is_swag_pack,
                          ) === 1 ||
                            projectSourcingOpsStore.productDetails?.product_details?.categories?.some(
                              (c) => {
                                return (
                                  c.category_id ===
                                  Number(
                                    process.env.REACT_APP_PACKAGING_CATEGORY_ID,
                                  )
                                );
                              },
                            )) && (
                            <>
                              <Grid2
                                container
                                spacing={{ xl: 10 }}
                                columns={{ lg: 12, xs: 12 }}
                                mt={"1.875rem"}
                                sx={{
                                  display: {
                                    xs: "block",
                                    lg: "block",
                                    xl: "flex",
                                  },
                                  flexWrap: "wrap",
                                  justifyContent: { xl: "space-between" },
                                }}
                              >
                                <Grid2
                                  size={{ xl: 6, lg: 12, xs: 12 }}
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "start",
                                  }}
                                >
                                  <Box>
                                    <CustomTypography
                                      variant="heading6"
                                      color={
                                        theme.palette.customColors.greyTextColor
                                      }
                                    >
                                      Box Sizing (L*B*H)
                                    </CustomTypography>
                                    <Box
                                      mt={"1rem"}
                                      sx={{
                                        width: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                      }}
                                    >
                                      <CustomTextField
                                        type="number"
                                        customStyles={{
                                          width: "5.625rem",
                                          border: `0.1px solid ${theme.palette.customColors.greyDarkborder}`,
                                          borderRadius: "4px",
                                        }}
                                        value={
                                          projectSourcingOpsStore.productDetails
                                            ?.box_sizing?.length || ""
                                        }
                                        placeholder=""
                                        onSearch={(length) => {
                                          projectSourcingOpsStore.updateProductDetails(
                                            "box_sizing",
                                            { length: Number(length) },
                                          );
                                        }}
                                        disabled={
                                          !projectSourcingOpsStore
                                            .productDetails
                                            ?.customisation_required ||
                                          isMutationLocked
                                        }
                                      />

                                      <IconButton size="small">
                                        <img src={xIcon} alt="x-icon" />
                                      </IconButton>

                                      <CustomTextField
                                        type="number"
                                        customStyles={{
                                          width: "5.625rem",
                                          border: `0.1px solid ${theme.palette.customColors.greyDarkborder}`,
                                          borderRadius: "4px",
                                        }}
                                        value={
                                          projectSourcingOpsStore.productDetails
                                            ?.box_sizing?.breadth || ""
                                        }
                                        placeholder=""
                                        onSearch={(breadth) => {
                                          projectSourcingOpsStore.updateProductDetails(
                                            "box_sizing",
                                            { breadth: Number(breadth) },
                                          );
                                        }}
                                        disabled={
                                          !projectSourcingOpsStore
                                            .productDetails
                                            ?.customisation_required ||
                                          isMutationLocked
                                        }
                                      />

                                      <IconButton size="small">
                                        <img src={xIcon} alt="x-icon" />
                                      </IconButton>

                                      <CustomTextField
                                        type="number"
                                        customStyles={{
                                          width: "5.625rem",
                                          border: `0.1px solid ${theme.palette.customColors.greyDarkborder}`,
                                          borderRadius: "4px",
                                        }}
                                        value={
                                          projectSourcingOpsStore.productDetails
                                            ?.box_sizing?.height || ""
                                        }
                                        placeholder=""
                                        onSearch={(height) => {
                                          projectSourcingOpsStore.updateProductDetails(
                                            "box_sizing",
                                            { height: Number(height) },
                                          );
                                        }}
                                        disabled={
                                          !projectSourcingOpsStore
                                            .productDetails
                                            ?.customisation_required ||
                                          isMutationLocked
                                        }
                                      />
                                    </Box>
                                  </Box>
                                </Grid2>

                                <Grid2
                                  size={{ xl: 6, lg: 12, xs: 12 }}
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "end",
                                    mt: { xl: 0, lg: 3, md: 3, sm: 3, xs: 3 },
                                  }}
                                >
                                  <Box>
                                    <CustomTypography
                                      variant="heading6"
                                      color={
                                        theme.palette.customColors.greyTextColor
                                      }
                                    >
                                      Outer Box Sizing (L*B*H)
                                    </CustomTypography>
                                    <Box
                                      mt={1.8}
                                      sx={{
                                        width: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                      }}
                                    >
                                      <CustomTextField
                                        type="number"
                                        customStyles={{
                                          width: "5.625rem",
                                          border: `0.1px solid ${theme.palette.customColors.greyDarkborder}`,
                                          borderRadius: "4px",
                                        }}
                                        value={
                                          projectSourcingOpsStore.productDetails
                                            ?.outer_box_sizing?.length || ""
                                        }
                                        placeholder=""
                                        onSearch={(length) => {
                                          projectSourcingOpsStore.updateProductDetails(
                                            "outer_box_sizing",
                                            { length: Number(length) },
                                          );
                                        }}
                                        disabled={
                                          !projectSourcingOpsStore
                                            .productDetails
                                            ?.customisation_required ||
                                          isMutationLocked
                                        }
                                      />

                                      <IconButton size="small">
                                        <img src={xIcon} alt="x-icon" />
                                      </IconButton>

                                      <CustomTextField
                                        type="number"
                                        customStyles={{
                                          width: "5.625rem",
                                          border: `0.1px solid ${theme.palette.customColors.greyDarkborder}`,
                                          borderRadius: "4px",
                                        }}
                                        value={
                                          projectSourcingOpsStore.productDetails
                                            ?.outer_box_sizing?.breadth || ""
                                        }
                                        placeholder=""
                                        onSearch={(breadth) => {
                                          projectSourcingOpsStore.updateProductDetails(
                                            "outer_box_sizing",
                                            { breadth: Number(breadth) },
                                          );
                                        }}
                                        disabled={
                                          !projectSourcingOpsStore
                                            .productDetails
                                            ?.customisation_required ||
                                          isMutationLocked
                                        }
                                      />

                                      <IconButton size="small">
                                        <img src={xIcon} alt="x-icon" />
                                      </IconButton>

                                      <CustomTextField
                                        type="number"
                                        customStyles={{
                                          width: "5.625rem",
                                          border: `0.1px solid ${theme.palette.customColors.greyDarkborder}`,
                                          borderRadius: "4px",
                                        }}
                                        value={
                                          projectSourcingOpsStore.productDetails
                                            ?.outer_box_sizing?.height || ""
                                        }
                                        placeholder=""
                                        onSearch={(height) => {
                                          projectSourcingOpsStore.updateProductDetails(
                                            "outer_box_sizing",
                                            { height: Number(height) },
                                          );
                                        }}
                                        disabled={
                                          !projectSourcingOpsStore
                                            .productDetails
                                            ?.customisation_required ||
                                          isMutationLocked
                                        }
                                      />
                                    </Box>
                                  </Box>
                                </Grid2>
                              </Grid2>
                            </>
                          )}
                      </>
                    );
                  })()}
                  {/*  */}

                  <Box
                    sx={{
                      mt: 3,
                      display: {
                        xl: "flex",
                        lg: "flex",
                        md: "block",
                        sm: "block",
                      },
                      justifyContent: {
                        xl: "space-between",
                        lg: "space-between",
                      },
                    }}
                  >
                    <CustomTypography variant="heading5" fontWeight={600}>
                      {`Select Product Variants (${linkedVariantsCount})`}
                    </CustomTypography>
                    <AppButton
                      label="Add Variant"
                      startIcon={<AddIcon />}
                      onClick={openVariantsDrawer}
                      customStyles={{
                        backgroundColor: "transparent",
                        padding: 1,
                        margin: 0,
                        fontSize: "1rem",
                        color: theme.palette.customColors.pinkDark,
                        "&:hover": {
                          backgroundColor: "transparent",
                          color: theme.palette.customColors.pinkDark,
                          boxShadow: "none",
                        },
                      }}
                      disabled={
                        isMutationLocked
                      }
                    />
                  </Box>

                  <Box
                    mt={"1.2rem"}
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: {
                        xl: "space-between",
                        lg: "space-between",
                      },
                    }}
                  >
                    {projectSourcingOpsStore.productDetails?.size_mix?.map(
                      (variant, index) => {
                        return (
                          <Grid2
                            key={
                              variant.id ??
                              `${index}-${variant.sku || "variant"}`
                            }
                            size={{ xs: 12, xl: 6 }}
                            sx={{ mt: "1.2rem" }}
                          >
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1.75,
                                borderRadius: 2,
                                borderColor: colors.greyBorder,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                }}
                              >
                                <Box
                                  minWidth={0}
                                  display="flex"
                                  flexDirection="column"
                                  gap={0.5}
                                >
                                  <CustomTypography
                                    variant="body2"
                                    fontWeight={700}
                                  >
                                    {variant.title || "—"}
                                  </CustomTypography>
                                  <SkuCopyRow sku={variant.sku} />
                                </Box>
                                <CustomTypography
                                  variant="body2"
                                  color={colors.greyTextColor}
                                >
                                  Qty: {variant.quantity ?? "—"}
                                </CustomTypography>
                              </Box>
                            </Paper>
                          </Grid2>
                        );
                      },
                    )}
                  </Box>

                  {(() => {
                    const product = projectSourcingOpsStore?.products?.find(
                      (p) => p.id === productId,
                    );

                    return (
                      <>
                        {product &&
                          projectSourcingOpsStore.productDetails &&
                          (Number(
                            projectSourcingOpsStore.productDetails
                              ?.product_details?.is_swag_pack,
                          ) === 1 ||
                            projectSourcingOpsStore.productDetails?.product_details?.categories?.some(
                              (c) => {
                                return (
                                  c.category_id ===
                                  Number(
                                    process.env.REACT_APP_PACKAGING_CATEGORY_ID,
                                  )
                                );
                              },
                            )) && (
                            <>
                              <Box mt={3.8}>
                                <CustomTypography
                                  variant="heading5"
                                  fontWeight={600}
                                >
                                  Product Dimensions
                                </CustomTypography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    justifyContent: "space-between",
                                    mt: "1.5rem",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: {
                                        xl: "45%",
                                        lg: "45%",
                                        md: "48%",
                                        sm: "100%",
                                        xs: "100%",
                                      },
                                    }}
                                  >
                                    <CustomTypography
                                      variant="heading6"
                                      color={
                                        theme.palette.customColors.greyTextColor
                                      }
                                    >
                                      Length (inches)
                                    </CustomTypography>
                                    <CustomTextField
                                      type="number"
                                      customStyles={{
                                        width: "100%",
                                        borderRadius: "4px",
                                        marginTop: "14px",
                                      }}
                                      borderColor={
                                        theme.palette.customColors
                                          .greyDarkborder
                                      }
                                      value={
                                        projectSourcingOpsStore.productDetails
                                          ?.product_details?.length || ""
                                      }
                                      placeholder=""
                                      onChange={(e) =>
                                        projectSourcingOpsStore.updateProductDetails(
                                          "product_details",
                                          {
                                            ...projectSourcingOpsStore
                                              .productDetails?.product_details,
                                            length: Number(e.target.value),
                                          },
                                        )
                                      }
                                      disabled={
                                        isMutationLocked
                                      }
                                    />
                                  </Box>
                                  <Box
                                    sx={{
                                      width: {
                                        xl: "45%",
                                        lg: "45%",
                                        md: "48%",
                                        sm: "100%",
                                        xs: "100%",
                                      },
                                    }}
                                  >
                                    <CustomTypography
                                      variant="heading6"
                                      color={
                                        theme.palette.customColors.greyTextColor
                                      }
                                    >
                                      Breadth (inches)
                                    </CustomTypography>
                                    <CustomTextField
                                      type="number"
                                      customStyles={{
                                        width: "100%",
                                        borderRadius: "4px",
                                        marginTop: "14px",
                                      }}
                                      borderColor={
                                        theme.palette.customColors
                                          .greyDarkborder
                                      }
                                      value={
                                        projectSourcingOpsStore.productDetails
                                          ?.product_details?.breadth || ""
                                      }
                                      placeholder=""
                                      onChange={(e) =>
                                        projectSourcingOpsStore.updateProductDetails(
                                          "product_details",
                                          {
                                            ...projectSourcingOpsStore
                                              .productDetails?.product_details,
                                            breadth: Number(e.target.value),
                                          },
                                        )
                                      }
                                      disabled={
                                        isMutationLocked
                                      }
                                    />
                                  </Box>
                                  <Box
                                    sx={{
                                      width: {
                                        xl: "45%",
                                        lg: "45%",
                                        md: "48%",
                                        sm: "100%",
                                        xs: "100%",
                                      },
                                      mt: "1.5rem",
                                    }}
                                  >
                                    <CustomTypography
                                      variant="heading6"
                                      color={
                                        theme.palette.customColors.greyTextColor
                                      }
                                    >
                                      Height (inches)
                                    </CustomTypography>
                                    <CustomTextField
                                      type="number"
                                      customStyles={{
                                        width: "100%",
                                        borderRadius: "4px",
                                        marginTop: "14px",
                                      }}
                                      borderColor={
                                        theme.palette.customColors
                                          .greyDarkborder
                                      }
                                      value={
                                        projectSourcingOpsStore.productDetails
                                          ?.product_details?.height || ""
                                      }
                                      placeholder=""
                                      onChange={(e) =>
                                        projectSourcingOpsStore.updateProductDetails(
                                          "product_details",
                                          {
                                            ...projectSourcingOpsStore
                                              .productDetails?.product_details,
                                            height: Number(e.target.value),
                                          },
                                        )
                                      }
                                      disabled={
                                        isMutationLocked
                                      }
                                    />
                                  </Box>
                                  <Box
                                    sx={{
                                      width: {
                                        xl: "45%",
                                        lg: "45%",
                                        md: "48%",
                                        sm: "100%",
                                        xs: "100%",
                                      },
                                      mt: "1.5rem",
                                    }}
                                  >
                                    <CustomTypography
                                      variant="heading6"
                                      color={
                                        theme.palette.customColors.greyTextColor
                                      }
                                    >
                                      Weight (kg)
                                    </CustomTypography>
                                    <CustomTextField
                                      type="number"
                                      customStyles={{
                                        width: "100%",
                                        borderRadius: "4px",
                                        marginTop: "14px",
                                      }}
                                      borderColor={
                                        theme.palette.customColors
                                          .greyDarkborder
                                      }
                                      value={
                                        projectSourcingOpsStore.productDetails
                                          ?.product_details
                                          ?.volumetric_weight || ""
                                      }
                                      placeholder=""
                                      onChange={(e) =>
                                        projectSourcingOpsStore.updateProductDetails(
                                          "product_details",
                                          {
                                            ...projectSourcingOpsStore
                                              .productDetails?.product_details,
                                            volumetric_weight: Number(
                                              e.target.value,
                                            ),
                                          },
                                        )
                                      }
                                      disabled={
                                        isMutationLocked
                                      }
                                    />
                                  </Box>
                                </Box>
                              </Box>

                              <Box mt={3.8}>
                                <CustomTypography
                                  variant="heading5"
                                  fontWeight={600}
                                >
                                  Boxed Dimensions
                                </CustomTypography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    justifyContent: "space-between",
                                    mt: "1.5rem",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: {
                                        xl: "45%",
                                        lg: "45%",
                                        md: "48%",
                                        sm: "100%",
                                        xs: "100%",
                                      },
                                    }}
                                  >
                                    <CustomTypography
                                      variant="heading6"
                                      color={
                                        theme.palette.customColors.greyTextColor
                                      }
                                    >
                                      Boxed Length (inches)
                                    </CustomTypography>
                                    <CustomTextField
                                      type="number"
                                      customStyles={{
                                        width: "100%",
                                        borderRadius: "4px",
                                        marginTop: "14px",
                                      }}
                                      borderColor={
                                        theme.palette.customColors
                                          .greyDarkborder
                                      }
                                      value={
                                        projectSourcingOpsStore.productDetails
                                          ?.product_details?.boxed_length || ""
                                      }
                                      placeholder=""
                                      onChange={(e) =>
                                        projectSourcingOpsStore.updateProductDetails(
                                          "product_details",
                                          {
                                            ...projectSourcingOpsStore
                                              .productDetails?.product_details,
                                            boxed_length: Number(
                                              e.target.value,
                                            ),
                                          },
                                        )
                                      }
                                      disabled={
                                        isMutationLocked
                                      }
                                    />
                                  </Box>
                                  <Box
                                    sx={{
                                      width: {
                                        xl: "45%",
                                        lg: "45%",
                                        md: "48%",
                                        sm: "100%",
                                        xs: "100%",
                                      },
                                    }}
                                  >
                                    <CustomTypography
                                      variant="heading6"
                                      color={
                                        theme.palette.customColors.greyTextColor
                                      }
                                    >
                                      Boxed Breadth (inches)
                                    </CustomTypography>
                                    <CustomTextField
                                      type="number"
                                      customStyles={{
                                        width: "100%",
                                        borderRadius: "4px",
                                        marginTop: "14px",
                                      }}
                                      borderColor={
                                        theme.palette.customColors
                                          .greyDarkborder
                                      }
                                      value={
                                        projectSourcingOpsStore.productDetails
                                          ?.product_details?.boxed_breadth || ""
                                      }
                                      placeholder=""
                                      onChange={(e) =>
                                        projectSourcingOpsStore.updateProductDetails(
                                          "product_details",
                                          {
                                            ...projectSourcingOpsStore
                                              .productDetails?.product_details,
                                            boxed_breadth: Number(
                                              e.target.value,
                                            ),
                                          },
                                        )
                                      }
                                      disabled={
                                        isMutationLocked
                                      }
                                    />
                                  </Box>
                                  <Box
                                    sx={{
                                      width: {
                                        xl: "45%",
                                        lg: "45%",
                                        md: "48%",
                                        sm: "100%",
                                        xs: "100%",
                                      },
                                      mt: "1.5rem",
                                    }}
                                  >
                                    <CustomTypography
                                      variant="heading6"
                                      color={
                                        theme.palette.customColors.greyTextColor
                                      }
                                    >
                                      Boxed Height (inches)
                                    </CustomTypography>
                                    <CustomTextField
                                      type="number"
                                      customStyles={{
                                        width: "100%",
                                        borderRadius: "4px",
                                        marginTop: "14px",
                                      }}
                                      borderColor={
                                        theme.palette.customColors
                                          .greyDarkborder
                                      }
                                      value={
                                        projectSourcingOpsStore.productDetails
                                          ?.product_details?.boxed_height || ""
                                      }
                                      placeholder=""
                                      onChange={(e) =>
                                        projectSourcingOpsStore.updateProductDetails(
                                          "product_details",
                                          {
                                            ...projectSourcingOpsStore
                                              .productDetails?.product_details,
                                            boxed_height: Number(
                                              e.target.value,
                                            ),
                                          },
                                        )
                                      }
                                      disabled={
                                        isMutationLocked
                                      }
                                    />
                                  </Box>
                                  <Box
                                    sx={{
                                      width: {
                                        xl: "45%",
                                        lg: "45%",
                                        md: "48%",
                                        sm: "100%",
                                        xs: "100%",
                                      },
                                      mt: "1.5rem",
                                    }}
                                  >
                                    <CustomTypography
                                      variant="heading6"
                                      color={
                                        theme.palette.customColors.greyTextColor
                                      }
                                    >
                                      Boxed Weight (kg)
                                    </CustomTypography>
                                    <CustomTextField
                                      type="number"
                                      customStyles={{
                                        width: "100%",
                                        borderRadius: "4px",
                                        marginTop: "14px",
                                      }}
                                      borderColor={
                                        theme.palette.customColors
                                          .greyDarkborder
                                      }
                                      value={
                                        projectSourcingOpsStore.productDetails
                                          ?.product_details
                                          ?.boxed_volumetric_weight || ""
                                      }
                                      placeholder=""
                                      onChange={(e) =>
                                        projectSourcingOpsStore.updateProductDetails(
                                          "product_details",
                                          {
                                            ...projectSourcingOpsStore
                                              .productDetails?.product_details,
                                            boxed_volumetric_weight: Number(
                                              e.target.value,
                                            ),
                                          },
                                        )
                                      }
                                      disabled={
                                        isMutationLocked
                                      }
                                    />
                                  </Box>
                                </Box>
                              </Box>
                            </>
                          )}
                      </>
                    );
                  })()}
                  {userStore.hasPermission(EmployeeRoles.CREATE_PROJECT) ||
                  userStore.hasPermission(EmployeeRoles.EDIT_PROJECT) ||
                  userStore.hasPermission(
                    EmployeeRoles.EDIT_PRODUCT_SOURCING_DETAILS,
                  ) ? (
                    <>
                      <Box
                        mt={3.8}
                        sx={{
                          width: { xl: "45.35%", lg: "100%", md: "100%" },
                          display: "flex",
                          gap: 1.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <AppButton
                          label="Save Details"
                          onClick={() => handleSaveAdditionalDetails()}
                          fontWeight={600}
                          fontSize="1rem"
                          variant="secondary"
                          customStyles={{ width: "100%" }}
                          disabled={
                            isMutationLocked
                          }
                        />
                      </Box>
                    </>
                  ) : (
                    <></>
                  )}

                  <Divider sx={{ mt: "2.125rem", mb: "1.688rem" }} />

                  <Box
                    sx={{
                      display: {
                        xl: "flex",
                        lg: "block",
                        md: "block",
                        sm: "block",
                      },
                      justifyContent: { xl: "space-between" },
                    }}
                  >
                    <Box
                      mb={3}
                      sx={{
                        width: { xl: "45.35%", lg: "100%", md: "100%" },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                          mb: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <CustomTypography
                          variant="heading5"
                          fontWeight={600}
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          Files for printing
                        </CustomTypography>
                        {printingSkuChoices.length > 1 ? (
                          <Box sx={{ width: 220, maxWidth: "100%", flexShrink: 0 }}>
                            <AppDropDown
                              title="SKU"
                              isMultiSelect={false}
                              list={printingSkuChoices}
                              value={printingFileSku}
                              onSingleSelectionChange={setPrintingFileSku}
                              placeholderText="All SKUs"
                              width="220px"
                              height="32px"
                              fontSize="0.75rem"
                            />
                          </Box>
                        ) : null}
                      </Box>
                      <CustomTypography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 1 }}
                      >
                        {printingSkuSelected
                          ? "Accepted formats: CDR, PDF, XLSX"
                          : "Select a SKU. New printing files are saved against that SKU."}
                      </CustomTypography>
                      <Box sx={{ width: "100%", maxWidth: "100%" }}>
                        <ImageUpload
                          minHeight="10.938rem"
                          size={"large"}
                          allowedFileTypes="image/*,.cdr,.pdf,.xlsx"
                          showImagesList={false}
                          multiple={true}
                          existingImageList={[]}
                          addImagesToBeUploaded={(file) => {
                            handleUpload(file);
                          }}
                          disabled={
                            !printingSkuSelected ||
                            !projectSourcingOpsStore.productDetails
                              ?.customisation_required ||
                            isMutationLocked
                          }
                        />
                      </Box>
                      {printingFilesForSku.length === 0 ? (
                        <CustomTypography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 1 }}
                        >
                          {printingSkuSelected
                            ? "No printing files for this SKU."
                            : "No printing files uploaded yet."}
                        </CustomTypography>
                      ) : null}
                    </Box>
                    {userStore.hasPermission(EmployeeRoles.UPLOAD_MOCKUPS) && (
                      <>
                        <Box
                          mb={3}
                          sx={{
                            width: { xl: "45.35%", lg: "100%", md: "100%" },
                          }}
                        >
                          {printingFilesForSku.length ? (
                            <Box display={"flex"} justifyContent={"start"}>
                              <CustomTypography
                                variant="heading6"
                                color={theme.palette.customColors.greyTextColor}
                              >
                                Uploaded files
                              </CustomTypography>
                            </Box>
                          ) : null}

                          <Box
                            mt={3}
                            sx={{
                              maxHeight: "10.938rem",
                              overflow: "auto",
                              "&::-webkit-scrollbar": {
                                width: "3px",
                              },
                              "&::-webkit-scrollbar-thumb": {
                                backgroundColor:
                                  theme.palette.customColors.purple,
                                borderRadius: "4px",
                              },
                              "&::-webkit-scrollbar-track": {
                                backgroundColor:
                                  theme.palette.customColors.lightPurple,
                              },
                            }}
                          >
                            {printingFilesForSku.map(
                              (file) => (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-evenly"
                                  mb={2}
                                  key={file.id}
                                  sx={{
                                    width: "100%",
                                    maxWidth: "100%",
                                    gap: 2,
                                  }}
                                >
                                  <IconButton
                                    color="secondary"
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      padding: 0,
                                    }}
                                  >
                                    <img src={cdricon} alt="File Icon" />
                                  </IconButton>

                                  <Box
                                    sx={{
                                      flex: "1 1 auto",
                                      minWidth: 0,
                                      textAlign: "center",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                    title={file.image_url}
                                  >
                                    <CustomTypography variant="heading8">
                                      {file.image_url}
                                    </CustomTypography>
                                    {!printingSkuSelected ? (
                                      <CustomTypography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {String(file.variant_sku ?? "").trim() ||
                                          "Product"}
                                      </CustomTypography>
                                    ) : null}
                                  </Box>

                                  <IconButton
                                    color="secondary"
                                    sx={{
                                      backgroundColor:
                                        theme.palette.customColors.pinkLight,
                                      width: 30,
                                      height: 30,
                                      marginRight: "1rem",
                                    }}
                                    onClick={() => handleDeleteFile(file.id)}
                                    disabled={
                                      isMutationLocked
                                    }
                                  >
                                    <DeleteIcon
                                      sx={{
                                        color:
                                          theme.palette.customColors.pinkDark,
                                        width: 20,
                                        height: 20,
                                      }}
                                    />
                                  </IconButton>
                                </Box>
                              ),
                            )}
                          </Box>
                        </Box>
                      </>
                    )}
                  </Box>
                </Grid2>
              </Grid2>
            </Box>

            <Drawer
              anchor="right"
              open={isCommentsDrawerOpen}
              onClose={() => setIsCommentsDrawerOpen(false)}
              sx={{
                zIndex: (t) => t.zIndex.drawer + 200,
                "& .MuiDrawer-paper": {
                  zIndex: (t) => t.zIndex.drawer + 200,
                },
              }}
            >
              <Box
                sx={{
                  width: { xs: "100vw", sm: 520, md: 600 },
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: theme.palette.customColors.greyBodyBackground,
                  backgroundImage: `linear-gradient(165deg, ${alpha(theme.palette.customColors.purple || "#6455ED", 0.08)} 0%, ${theme.palette.customColors.greyBodyBackground} 38%, ${alpha(theme.palette.customColors.purple || "#6455ED", 0.04)} 100%)`,
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    backgroundColor: alpha(
                      theme.palette.background.paper,
                      0.72,
                    ),
                    backdropFilter: "blur(8px)",
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box minWidth={0}>
                    <CustomTypography
                      variant="heading5"
                      fontWeight={700}
                      sx={{ fontSize: "1rem" }}
                    >
                      Comments
                    </CustomTypography>
                    <CustomTypography
                      variant="heading8"
                      color={theme.palette.customColors.greyTextColor}
                      sx={{ mt: 0.25, fontSize: "0.68rem" }}
                    >
                      {projectSourcingOpsStore.productDetails?.name ||
                        "Product comments"}
                    </CustomTypography>
                  </Box>
                  <IconButton
                    onClick={() => setIsCommentsDrawerOpen(false)}
                    aria-label="Close comments"
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    px: 2.5,
                    pb: 1.5,
                    pt: 1,
                  }}
                >
                  <ProjectProductComments
                    projectId={projectId}
                    productId={productId}
                    open={isCommentsDrawerOpen}
                    hideTitle
                    archived={isMutationLocked}
                  />
                </Box>
              </Box>
            </Drawer>

            {userStore.hasPermission(EmployeeRoles.VIEW_ORDER) &&
              openMockupsModal &&
              !confirmationPopup && (
                <OrderUnifiedMockupModal
                  open={openMockupsModal}
                  onClose={closeMockupGenerator}
                  activeTab={mockupModalTab}
                  onTabChange={handleMockupTabChange}
                  mockupId={generatorMockupId}
                  productId={productId}
                  orderId={projectId}
                  productName={projectSourcingOpsStore.productDetails?.name}
                  archived={isMutationLocked ? 1 : 0}
                  canDeleteMockup={userStore.hasPermission(
                    EmployeeRoles.DELETE_MOCKUP,
                  )}
                  mockups={orderStore.productMockup || []}
                  comments={orderStore.mockupComment || []}
                  updateMockupComment={(mockupComment) => {
                    setIsCommentsFetching(true);
                    postMockupComment(mockupComment);
                  }}
                  fetchMockupComment={(mockupId: number) => {
                    setIsCommentsFetching(true);
                    fetchMockupComment(mockupId);
                  }}
                  isCommentsFetching={isCommentsFetching}
                  deleteMockup={(mockupId: number) => {
                    deleteMockup(mockupId);
                  }}
                  selectedMockupId={
                    projectSourcingOpsStore.selectedMockupId ?? undefined
                  }
                  isMainMockupImageLoading={isMainMockupImage}
                  onSelectPsdId={(id) => {
                    mockupConfigStore.resetGeneratedMockupData();
                    mockupConfigStore.setSelectedPsdId(id);
                  }}
                  onGenerateMockup={handleOrderGenerateMockup}
                  onUploadLogoToLibrary={handleOrderUploadLogoToLibrary}
                  onDeleteUploadedLogo={handleOrderDeleteUploadedLogo}
                  onManualUploadMockup={
                    generatorMockupId
                      ? handleOrderManualUploadMockup
                      : undefined
                  }
                  onUploadMockupFiles={handleOrderUploadMockupFiles}
                />
              )}

            {confirmationPopup && (
              <ConfirmationPopup
                open={confirmationPopup}
                id={projectSourcingOpsStore.selectedMockupId!}
                title="Confirm Action"
                content="Are you sure you want to delete this mockup?"
                onConfirm={() => {
                  deleteMockup(projectSourcingOpsStore.selectedMockupId!);
                }}
                onCancel={() => {
                  projectSourcingOpsStore.setSelectedMockupId(null);
                  setConfirmationPopup(false);
                }}
              />
            )}
            {confirmationMailToClient && (
              <ConfirmationPopup
                open={confirmationMailToClient}
                id={projectId}
                title="Confirm Send Email"
                content="Are you sure you want to send the email to the client?"
                onConfirm={() => {
                  handleSendEmailToClient();
                  setConfirmationMailToClient(false);
                }}
                onCancel={() => setConfirmationMailToClient(false)}
              />
            )}

            <ImageGalleryPopup
              key={"productImages"}
              open={openImageGalleryModal}
              onClose={() => setOpenImageGalleryModal(false)}
              images={
                previewImage
                  ? [
                      {
                        src: previewImage,
                      },
                    ]
                  : []
              }
              title={"Product Images"}
            />
            <OrderProductVariantsDrawer
              open={isVariantsDrawerOpen}
              product_id={productId}
              order_id={projectId}
              total_quantity={selectedProduct?.quantity || 0}
              productDetails={productStore.productDetails || null}
              onClose={closeVariantsDrawer}
              onSubmit={handleVariantsSubmit}
              onVariantsCountChange={(count) => {
                setLinkedVariantsCount(count);
              }}
              onSaveVariant={() => {
                getSourcingDetails();
              }}
              onDeleteVariant={() => {
                getSourcingDetails();
              }}
            />

            {confirmationMailToPrinter && (
              <ConfirmationPopup
                open={confirmationMailToPrinter}
                id={productId}
                title="Confirm Send Email"
                content="Are you sure you want to send the email to the printer?"
                onConfirm={() => {
                  handleSendEmailToPrinter();
                  setConfirmationMailToPrinter(false);
                }}
                onCancel={() => setConfirmationMailToPrinter(false)}
              />
            )}
          </Container>
        </>
      ) : (
        <>
          <PermissionBlockLabel />
        </>
      )}
    </>
  );
};

export default observer(SourcingOpsDetails);
